import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Calendar, Clock, MoreVertical, Briefcase, DollarSign, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

interface SortableProjectCardProps {
  id: string;
  name: string;
  client: string;
  deadline: string;
  deadlineNear?: boolean;
  hoursSpent: number;
  billableHours?: number;
  nonBillableHours?: number;
  billableValue?: number;
  status: "backlog" | "in_progress" | "review" | "done";
  billingType?: "hourly" | "fixed";
  fixedPrice?: number | null;
  completionPercentage?: number;
  estimatedHours?: number;
  hourlyRate?: number;
  onClick?: () => void;
}

const statusStyles = {
  backlog: {
    badge: "bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-600",
    accent: "from-slate-400 to-slate-500",
  },
  in_progress: {
    badge: "bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900 dark:text-blue-300 dark:border-blue-700",
    accent: "from-blue-400 to-blue-600",
  },
  review: {
    badge: "bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900 dark:text-amber-300 dark:border-amber-700",
    accent: "from-amber-400 to-amber-600",
  },
  done: {
    badge: "bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900 dark:text-emerald-300 dark:border-emerald-700",
    accent: "from-emerald-400 to-emerald-600",
  },
};

const statusLabels = {
  backlog: "Backlog",
  in_progress: "Em Andamento",
  review: "Revisão",
  done: "Concluído",
};

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

export function SortableProjectCard({
  id,
  name,
  client,
  deadline,
  deadlineNear,
  hoursSpent,
  billableHours = 0,
  nonBillableHours = 0,
  billableValue = 0,
  status,
  billingType = "hourly",
  fixedPrice,
  completionPercentage = 0,
  estimatedHours = 0,
  hourlyRate = 0,
  onClick,
}: SortableProjectCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const statusStyle = statusStyles[status];
  
  // Calculate progress for hourly projects
  const hourlyProgress = estimatedHours > 0 ? Math.min((hoursSpent / estimatedHours) * 100, 100) : 0;
  
  // Calculate billed amount for hourly projects
  const billedAmount = hoursSpent * hourlyRate;
  
  // Calculate real hourly rate for fixed price projects
  const realHourlyRate = fixedPrice && hoursSpent > 0 ? fixedPrice / hoursSpent : null;

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        "cursor-grab active:cursor-grabbing touch-none shadow-sm hover:shadow-lg transition-all duration-200 hover:-translate-y-1 overflow-hidden",
        isDragging && "opacity-70 shadow-xl z-50 rotate-2 scale-105 ring-2 ring-primary ring-offset-2"
      )}
      onClick={onClick}
    >
      {/* Top accent bar */}
      <div className={cn("h-1.5 w-full bg-gradient-to-r", statusStyle.accent)} />
      
      <CardHeader className="pb-2 pt-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0 space-y-1">
            <h3 className="font-semibold text-sm leading-tight line-clamp-2">{name}</h3>
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Briefcase className="h-3 w-3 flex-shrink-0" />
              <span className="text-xs truncate">{client}</span>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-7 w-7 flex-shrink-0"
                onPointerDown={(e) => e.stopPropagation()}
              >
                <MoreVertical className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem>Editar</DropdownMenuItem>
              <DropdownMenuItem>Arquivar</DropdownMenuItem>
              <DropdownMenuItem className="text-destructive">Excluir</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0 pb-3 space-y-2.5">
        {/* Billing Type Badge */}
        <div className="flex items-center gap-2 flex-wrap">
          <Badge 
            variant="outline" 
            className={cn(
              "text-xs",
              billingType === "fixed" 
                ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800" 
                : "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800"
            )}
          >
            {billingType === "fixed" ? "💰 Fixo" : "⏱️ Por Hora"}
          </Badge>
          {deadlineNear && deadline && (
            <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800">
              <AlertTriangle className="h-3 w-3 mr-1" />
              Prazo próximo
            </Badge>
          )}
        </div>

        {/* Billing Info based on type */}
        {billingType === "fixed" ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Valor do projeto</span>
              <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                {formatCurrency(fixedPrice || 0)}
              </span>
            </div>
            {/* Completion Progress */}
            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Conclusão</span>
                <span className="font-medium">{completionPercentage}%</span>
              </div>
              <Progress value={completionPercentage} className="h-1.5" />
            </div>
            {hoursSpent > 0 && (
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{hoursSpent}h trabalhadas</span>
                {realHourlyRate && (
                  <span className="font-medium">~{formatCurrency(realHourlyRate)}/h</span>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {/* Hours Progress */}
            {estimatedHours > 0 && (
              <div className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">{hoursSpent}h de {estimatedHours}h</span>
                  <span className="font-medium">{Math.round(hourlyProgress)}%</span>
                </div>
                <Progress 
                  value={hourlyProgress} 
                  className={cn("h-1.5", hourlyProgress > 100 && "bg-destructive/20")} 
                />
              </div>
            )}
            {/* Billable vs Non-Billable breakdown */}
            {(billableHours > 0 || nonBillableHours > 0) && (
              <div className="space-y-1 text-xs">
                {billableHours > 0 && (
                  <div className="flex items-center justify-between text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <DollarSign className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
                      Faturável
                    </span>
                    <span className="font-medium text-emerald-600 dark:text-emerald-400">
                      {billableHours}h • {formatCurrency(billableValue)}
                    </span>
                  </div>
                )}
                {nonBillableHours > 0 && (
                  <div className="flex items-center justify-between text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Não faturável
                    </span>
                    <span className="font-medium">{nonBillableHours}h</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* Deadline and Hours */}
        <div className="flex items-center gap-3 text-xs text-muted-foreground pt-1">
          {deadline && (
            <div className={cn(
              "flex items-center gap-1",
              deadlineNear && "text-amber-600 dark:text-amber-400 font-medium"
            )}>
              <Calendar className="h-3 w-3 flex-shrink-0" />
              <span>{deadline}</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3 flex-shrink-0" />
            <span>{hoursSpent}h</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
